import PropTypes from 'prop-types';
import React, { MouseEventHandler, ReactNode } from 'react';

import {
  ARROW_POSITION_LEFT,
  ARROW_POSITION_RIGHT,
  arrowPositionList,
  Color,
  ComponentSize,
  FontSize,
  fontSizeList,
  FontWeight,
  fontWeightList,
  IconPosition,
  IDesignTokens,
} from '../../../typings/design-tokens';
import { IClassNames, makeClassNames } from '../../../utils/styles';
import { iconSizeList, IIcon, VerticalAlign, verticalAlignList } from '../icons/icon';
import IconMoveRight from '../icons/icon-move-right';

import './cta-button.scss';

export interface ICtaButton extends Omit<IDesignTokens, 'color'> {
  children: ReactNode;
  className?: string;
  Icon?: React.FC<IIcon>;
  iconPosition?: IconPosition;
  iconSize?: ComponentSize;
  verticalAlign?: VerticalAlign;
  onClick?: MouseEventHandler | undefined;
}

const DEFAULT_COLOR: Color = 'light';
const DEFAULT_ICON_SIZE: ComponentSize = 'small';
const DEFAULT_FONT_SIZE: FontSize = '14';
const DEFAULT_FONT_WEIGHT: FontWeight = '500';

const DEFAULT_ICON_ALIGNMENT: VerticalAlign = 'middle';

const hasLeft = (iconPosition: IconPosition): boolean => iconPosition === ARROW_POSITION_LEFT;
const hasRight = (iconPosition: IconPosition): boolean => iconPosition === ARROW_POSITION_RIGHT;

const classNames = ({ colorToUse, fontSize, fontWeight }: IClassNames): IClassNames =>
  makeClassNames({
    ctaButton: [
      'cta-btn',
      colorToUse && `cta-btn-text--color-${colorToUse}`,
      fontSize && `cta-btn-text--font-size-${fontSize}`,
      fontWeight && `cta-btn-text--weight-${fontWeight}`,
    ],
    prepend: 'cta-btn__prepend',
    append: 'cta-btn__append',
  });

const CtaButton: React.FC<ICtaButton> = ({
  iconSize = DEFAULT_ICON_SIZE,
  fontSize = DEFAULT_FONT_SIZE,
  fontWeight = DEFAULT_FONT_WEIGHT,
  verticalAlign = DEFAULT_ICON_ALIGNMENT,
  Icon = IconMoveRight,
  onClick,
  iconPosition,
  children,
  className,
}) => {
  const colorToUse = DEFAULT_COLOR;
  const cn = classNames({ colorToUse, fontWeight, fontSize });

  const arrowLeft =
    iconPosition && hasLeft(iconPosition) ? (
      <span className={cn.prepend}>
        <Icon size={iconSize} color={colorToUse} reverse verticalAlign={verticalAlign} />
      </span>
    ) : null;

  const arrowRight =
    iconPosition && hasRight(iconPosition) ? (
      <span className={cn.append}>
        <Icon size={iconSize} color={colorToUse} verticalAlign={verticalAlign} />
      </span>
    ) : null;

  return (
    <button type="button" onClick={onClick} className={[cn.ctaButton, className].filter(Boolean).join(' ')}>
      {arrowLeft}
      {children}
      {arrowRight}
    </button>
  );
};

/* istanbul ignore else */ if (process.env.NODE_ENV !== 'production') {
  CtaButton.propTypes = {
    children: PropTypes.node.isRequired,
    className: PropTypes.string,
    fontSize: PropTypes.oneOf(fontSizeList),
    fontWeight: PropTypes.oneOf(fontWeightList),
    Icon: PropTypes.func,
    iconPosition: PropTypes.oneOf(arrowPositionList),
    iconSize: PropTypes.oneOf(iconSizeList),
    onClick: PropTypes.func,
    verticalAlign: PropTypes.oneOf(verticalAlignList),
  };
}

export default CtaButton;
